import { forwardRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseBadgeProps } from './types';
import { getBaseBadgeToneClassName, resolveBaseAppearance } from './theme';
import { cn } from './utils';

export const BaseBadge = forwardRef<HTMLSpanElement, BaseBadgeProps>(
  function BaseBadge(
    {
      children,
      label,
      icon,
      tone,
      appearance,
      variant,
      density,
      state,
      className,
      classNames,
      style,
      ...badgeProps
    },
    ref,
  ) {
    if (
      tone !== undefined &&
      tone !== 'default' &&
      tone !== 'primary' &&
      tone !== 'success' &&
      tone !== 'warning' &&
      tone !== 'danger' &&
      tone !== 'muted'
    ) {
      throw new TypeError(
        'BaseBadge tone must be "default", "primary", "success", "warning", "danger", or "muted".',
      );
    }

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance({
      ...resolvedAppearance,
      variant,
      density,
      state,
    });
    const resolvedLabel = children ?? label;
    const resolvedTone = tone ?? 'default';

    return (
      <span
        {...componentAttributes}
        {...badgeProps}
        ref={ref}
        data-monkeys-component="badge"
        data-slot="root"
        data-tone={resolvedTone}
        data-variant={theme.variant}
        data-density={theme.density}
        data-state={theme.state}
        className={cn(
          theme.slots.badgeBase,
          getBaseBadgeToneClassName(theme, tone),
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.badge,
          ...style,
        }}
      >
        {icon ? (
          <span
            data-slot="icon"
            data-tone={resolvedTone}
            className={cn(theme.slots.badgeIcon, classNames?.icon)}
          >
            {icon}
          </span>
        ) : null}
        {resolvedLabel !== undefined && resolvedLabel !== null ? (
          <span
            data-slot="label"
            data-tone={resolvedTone}
            className={cn(theme.slots.badgeLabel, classNames?.label)}
          >
            {resolvedLabel}
          </span>
        ) : null}
      </span>
    );
  },
);
